import React, { useState, useEffect } from 'react';
import { TextField, Radio, AppBar, Toolbar, Typography, Container, Paper, Box, Modal, RadioGroup, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Grid, Checkbox, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const editChargeInfoSections = [
    {
        title: "Charge Name",
        color: "#1976D2",
        description: "Enter or update the name of the charge. This is the label that will appear across fee-related screens.",
        fields: ["Text input field", "Pre-filled with the existing charge name on load"],
    },
    {
        title: "Charge Type",
        color: "#7B1FA2",
        description: "Select whether this charge is mandatory for all students or optional.",
        fields: ["Required – Charge is mandatory", "Optional – Charge is not mandatory"],
    },
    {
        title: "Student Applicability",
        color: "#388E3C",
        description: "Define which category of students this charge applies to. Both can be selected simultaneously.",
        fields: ["New Student – Applies to newly admitted students", "Old Student – Applies to existing/returning students"],
    },
    {
        title: "Charge Applicable On",
        color: "#F57C00",
        description: "Select whether this charge is applied at the time of admission or on a monthly recurring basis.",
        fields: ["Admission – One-time charge at the time of admission", "Monthly – Recurring charge applied every month"],
    },
    {
        title: "Month Selection",
        color: "#0277BD",
        description: "If the charge is Monthly, select which months it applies to. You can pick all months at once or choose specific ones.",
        fields: [
            "All Months – Automatically selects and locks all months",
            "Selected Months – Allows manual selection of individual months",
            "Month checkboxes are disabled when 'All Months' is chosen",
        ],
    },
    {
        title: "Actions",
        color: "#C62828",
        description: "Use the action buttons to save your changes or discard them and go back.",
        fields: [
            "SAVE – Submits the updated charge details via PUT request",
            "CANCEL – Discards changes and navigates back to the previous page",
        ],
    },
];

function EditChargeMaster() {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    // ── Info dialog state ──
    const [infoOpen, setInfoOpen] = useState(false);
    let navigate = useNavigate();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [chargeName, setChargeName] = useState('');
    const [chargeType, setChargeType] = useState('required');
    const [isNewStudent, setIsNewStudent] = useState(false);
    const [isOldStudent, setIsOldStudent] = useState(false);
    const [admissionType, setAdmissionType] = useState('admission');
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [months, setMonths] = useState([]);
    const [monthSelectionType, setMonthSelectionType] = useState('selected-month');
    const { classId } = useParams();

    useEffect(() => {
        // Fetch the available months for selection
        const fetchMonths = async () => {
            try {
                const apiUrl = process.env.REACT_APP_BASE_URL;
                const response = await fetch(`${apiUrl}/Fine/GetMonthList`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: sessionStorage.getItem('token'),
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setMonths(data);
                } else {
                    // alert('Failed to fetch months');
                    setSnackbarMessage('Failed to fetch months');
                    setOpenSnackbar(true);
                    setSnackbarSeverity("error");
                }
            } catch (error) {
                console.error('API request error:', error);
                // alert('An error occurred while fetching months.');
                setSnackbarMessage('An error occurred while fetching months.');
                setOpenSnackbar(true);
                setSnackbarSeverity("error");
            }
        };

        // Fetch existing charge details to pre-fill form fields
        const fetchChargeDetails = async () => {
            try {
                const apiUrl = process.env.REACT_APP_BASE_URL;
                const response = await fetch(
                    `${apiUrl}/ChargeMaster/FetchChargeCreationById/${classId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: sessionStorage.getItem('token'),
                        },
                    }
                );
                if (response.ok) {
                    const data = await response.json();

                    // Pre-fill fields based on response
                    setChargeName(data.chargeName);
                    setChargeType(data.required === 1 ? 'required' : 'optional');
                    setIsNewStudent(data.newStudent === 1);
                    setIsOldStudent(data.oldStudent === 1);
                    setAdmissionType(data.chargeType);

                    // Pre-select the months from the API response
                    const selectedMonthNames = data.monthDetails.map(month => month.monthName);
                    setSelectedMonths(selectedMonthNames);
                } else {
                    // alert('Failed to fetch charge details');
                    setSnackbarMessage('Failed to fetch charge details');
                    setOpenSnackbar(true);
                    setSnackbarSeverity("error");
                }
            } catch (error) {
                console.error('API request error:', error);
                // alert('An error occurred while fetching charge details.');
                setSnackbarMessage('An error occurred while fetching charge details.');
                setOpenSnackbar(true);
                setSnackbarSeverity("error");
            }
        };

        fetchMonths();
        fetchChargeDetails();
    }, [classId]);

    const handleSave = async (e) => {
        e.preventDefault();

        const payload = {
            chargeName,
            chargeType: admissionType,
            required: chargeType === 'required' ? 1 : 0,
            newStudent: isNewStudent ? 1 : 0,
            oldStudent: isOldStudent ? 1 : 0,
            monthData: months.map(month => ({
                isChecked: selectedMonths.includes(month.month),
                monthId: String(month.monthId)
            }))
        };

        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(
                `${apiUrl}/ChargeMaster/Id?Id=${classId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: sessionStorage.getItem('token'),
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (response.ok) {
                setShowSuccessModal(true);
            } else {
                // alert('Unable to update charge master');
                setSnackbarMessage('Unable to update charge master');
                setOpenSnackbar(true);
                setSnackbarSeverity("error");
            }
        } catch (error) {
            console.error('API request error:', error);
            // alert('An error occurred. Please try again later.');
            setSnackbarMessage('An error occurred. Please try again later');
            setOpenSnackbar(true);
            setSnackbarSeverity("error");
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleAllMonthsSelect = () => {
        setSelectedMonths(months.map(month => month.month));
    };

    const handleMonthSelectionChange = (event) => {
        const value = event.target.value;
        setMonthSelectionType(value);
        if (value === 'all-months') {
            handleAllMonthsSelect();
        } else {
            setSelectedMonths([]);
        }
    };

    const handleMonthCheckboxChange = (monthName) => {
        setSelectedMonths(prev => {
            if (prev.includes(monthName)) {
                return prev.filter(item => item !== monthName);
            } else {
                return [...prev, monthName];
            }
        });
    };

    return (
        <Container>
            <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
                <Toolbar>
                    <Typography variant="h4" component="div" style={{ fontWeight: 'bold', flexGrow: 1 }}>
                        Edit Charge Details
                    </Typography>

                    {/* ── Info Button ── */}
                    <IconButton
                        color="inherit"
                        onClick={() => setInfoOpen(true)}
                        title="Form Information"
                        style={{
                            backgroundColor: "rgba(255,255,255,0.15)",
                            border: "2px solid rgba(255,255,255,0.6)",
                            borderRadius: "50%",
                            padding: "6px",
                        }}
                    >
                        <InfoIcon style={{ fontSize: 26 }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* ── Info Dialog ── */}
            <Dialog
                open={infoOpen}
                onClose={() => setInfoOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
            >
                <DialogTitle
                    style={{
                        backgroundColor: "#0B1F3D",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "16px 24px",
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <InfoIcon style={{ marginRight: 8 }} />
                        <Typography variant="h6" style={{ fontWeight: "bold" }}>
                            Edit Charge Details — Form Guide
                        </Typography>
                    </Box>
                  
                </DialogTitle>

                <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                        This form allows you to update an existing charge record. All fields are pre-filled with the current values. Modify as needed and click SAVE to apply changes.
                    </Typography>

                    {editChargeInfoSections.map((section, index) => (
                        <Box
                            key={index}
                            style={{
                                backgroundColor: "#fff",
                                borderRadius: 12,
                                padding: "18px 20px",
                                marginBottom: 16,
                                borderLeft: `5px solid ${section.color}`,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                            }}
                        >
                            <Typography
                                variant="h6"
                                style={{ fontWeight: "bold", color: section.color, marginBottom: 6 }}
                            >
                                {section.title}
                            </Typography>

                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                                {section.description}
                            </Typography>

                            <Divider style={{ marginBottom: 10 }} />

                            <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>
                                📝 DETAILS
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {section.fields.map((field, i) => (
                                    <Chip
                                        key={i}
                                        label={field}
                                        size="small"
                                        variant="outlined"
                                        style={{
                                            borderColor: section.color,
                                            color: section.color,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </DialogContent>

                <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
                    <Button
                        onClick={() => setInfoOpen(false)}
                        variant="contained"
                        style={{ backgroundColor: "#0B1F3D", color: "#fff", borderRadius: 20, paddingInline: 28 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={2} style={{ marginTop: 3 }}>
                <Grid item xs={6}>
                    <Paper elevation={3} style={{ padding: 16 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Charge Name"
                                    value={chargeName}
                                    onChange={(e) => setChargeName(e.target.value)}
                                    fullWidth
                                    placeholder="Enter Charge Name"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Charge Type</Typography>
                                <RadioGroup
                                    aria-label="charge-type"
                                    name="charge-type"
                                    value={chargeType}
                                    onChange={(e) => setChargeType(e.target.value)}
                                    row
                                >
                                    <FormControlLabel value="required" control={<Radio />} label="Required" />
                                    <FormControlLabel value="optional" control={<Radio />} label="Optional" />
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={<Checkbox checked={isNewStudent} onChange={(e) => setIsNewStudent(e.target.checked)} />}
                                    label="New Student"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={<Checkbox checked={isOldStudent} onChange={(e) => setIsOldStudent(e.target.checked)} />}
                                    label="Old Student"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Charge Applicable on</Typography>
                                <RadioGroup
                                    aria-label="admission-type"
                                    name="admission-type"
                                    value={admissionType}
                                    onChange={(e) => setAdmissionType(e.target.value)}
                                    row
                                >
                                    <FormControlLabel value="Admission" control={<Radio />} label="Admission" />
                                    <FormControlLabel value="Monthly" control={<Radio />} label="Monthly" />
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Month Selection</Typography>
                                <RadioGroup
                                    aria-label="month-selection"
                                    name="month-selection"
                                    value={monthSelectionType}
                                    onChange={handleMonthSelectionChange}
                                    row
                                >
                                    <FormControlLabel value="all-months" control={<Radio />} label="All Months" />
                                    <FormControlLabel value="selected-month" control={<Radio />} label="Selected Months" />
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={12} style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                <Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
                                <Button variant="contained" color="error" onClick={handleCancel}>Cancel</Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={6}>
                    <Paper elevation={3} style={{ padding: 16, height: '100%', overflowY: 'auto' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Select</TableCell>
                                        <TableCell>Month</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {months.map((month, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectedMonths.includes(month.month)}
                                                    onChange={() => handleMonthCheckboxChange(month.month)}
                                                    disabled={monthSelectionType === 'all-months'}
                                                />
                                            </TableCell>
                                            <TableCell>{month.month}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <Modal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                aria-labelledby="success-modal-title"
                aria-describedby="success-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
                        <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
                            Charge Updated ✅
                        </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: '20px' }}>
                        <Typography id="success-modal-description" sx={{ mb: 2 }}>
                            The Charge has been updated successfully.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    navigate(-1); // go back after closing modal
                                }}
                                variant="contained"
                                color="primary"
                            >
                                OK
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default EditChargeMaster;